import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Phone,
  PhoneCall,
  PhoneOff,
  Loader2,
  User,
  Play,
  Square,
  Volume2,
  Sparkles,
  RefreshCw,
  CheckCircle2,
  Clock,
  TrendingUp,
  AlertTriangle,
  Copy,
  ChevronDown,
  ChevronUp,
  Mic,
  FileText,
  Calendar,
  MapPin,
  Bot,
  Smile,
  MessageSquare,
  Voicemail,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  ActionBackButton,
  LeadInfoCard,
  ActionPageHeader,
  ActionStatsGrid,
  ActionTipsList,
  SectionHeader,
  ActionTabs,
  HistoryItemCard,
  EmptyState,
} from "../../components/admin/ai-actions";

const API = "/api";

export default function AICallPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callHistory, setCallHistory] = useState(null);
  const [allCalls, setAllCalls] = useState([]);
  const [phase, setPhase] = useState("ready"); // ready, calling, completed
  const [callMode, setCallMode] = useState("ai"); // ai or manual
  const [callStatus, setCallStatus] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callLoading, setCallLoading] = useState(false);
  const [showScriptPreview, setShowScriptPreview] = useState(false);
  const [activeTab, setActiveTab] = useState("history"); // history, tips, automation
  const timerRef = useRef(null);
  
  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLead();
    fetchCallHistory();
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [leadId, token]);

  const fetchLead = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
      } else {
        toast.error("Lead not found");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  const fetchCallHistory = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/call-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCallHistory(data);
        setAllCalls(data.call_history || []);
      }
    } catch (error) {
      console.error("Failed to fetch call history:", error);
    }
  };

  const startAICall = async () => {
    const phoneNumber = lead?.mobile_phone || lead?.company_phone;
    if (!phoneNumber) {
      toast.error("This lead has no phone number");
      return;
    }

    setCallLoading(true);
    setPhase("calling");
    setCallStatus("initiating");

    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/call`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCallStatus("connected");
        toast.success(`AI call initiated to ${lead.first_name}!`);
        
        timerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);

        pollCallStatus();
      } else {
        const error = await response.json();
        setPhase("ready");
        if (response.status === 503) {
          toast.error("Retell AI not configured", {
            description: "Contact admin to enable AI calls"
          });
        } else {
          toast.error(error.detail || "Failed to initiate call");
        }
      }
    } catch (error) {
      setPhase("ready");
      toast.error("Network error - please try again");
    } finally {
      setCallLoading(false);
    }
  };

  const pollCallStatus = async () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API}/admin/leads/${leadId}/call-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCallHistory(data);
          setAllCalls(data.call_history || []);
          
          if (data.call_status === "ended" || data.call_status === "completed") {
            clearInterval(pollInterval);
            clearInterval(timerRef.current);
            setPhase("completed");
            setCallStatus("completed");
          }
        }
      } catch (error) {
        console.error("Poll error:", error);
      }
    }, 5000);

    setTimeout(() => clearInterval(pollInterval), 600000);
  };

  const endCall = () => {
    clearInterval(timerRef.current);
    setPhase("completed");
    setCallStatus("ended");
    toast.info("Call ending...");
    setTimeout(fetchCallHistory, 2000);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getLastContactDays = () => {
    if (!callHistory?.last_call_at) return "Never";
    const lastCall = new Date(callHistory.last_call_at);
    const now = new Date();
    const days = Math.floor((now - lastCall) / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  };

  const getConnectedCalls = () => {
    return allCalls.filter(c => c.status === "connected" || c.outcome === "connected").length;
  };

  const getAvgDuration = () => {
    if (allCalls.length === 0) return "0:00";
    const totalSeconds = allCalls.reduce((sum, c) => sum + (c.duration || 0), 0);
    const avg = Math.floor(totalSeconds / allCalls.length);
    return formatDuration(avg);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  const phoneNumber = lead?.mobile_phone || lead?.company_phone;

  // Stats for the grid
  const callStats = [
    { value: callHistory?.total_calls_made || 0, label: "Total Calls", color: "text-white" },
    { value: getConnectedCalls(), label: "Connected", color: "text-green-400" },
    { value: getAvgDuration(), label: "Avg Duration", color: "text-white" },
    { value: callHistory?.sentiment_summary?.positive || 0, label: "Positive", color: "text-cyan-400" },
  ];

  // Tabs for the right column
  const tabs = [
    { id: "history", label: "History" },
    { id: "tips", label: "Tips" },
    { id: "automation", label: "Automation" },
  ];

  // Tips for calls
  const callTips = [
    "Best call times: 10-11 AM and 4-5 PM local time",
    "Prepare key talking points before calling",
    "Listen more than you talk (70/30 rule)",
    "Always confirm next steps before ending",
    "Log call notes immediately after",
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-6">
          {/* Back Button - Using shared component */}
          <ActionBackButton leadId={leadId} leadName={lead?.first_name} />
          
          {/* Page Header - Using shared component */}
          <ActionPageHeader
            icon={Phone}
            title="AI Call Center"
            subtitle={`Voice outreach for ${lead?.first_name} ${lead?.last_name}`}
            gradientFrom="pink-500"
            gradientTo="orange-500"
          />
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="col-span-7 space-y-6">
            {/* Lead Info Card */}
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
              <div className="flex gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center">
                    <User className="w-8 h-8 text-purple-300" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-xl font-bold text-white">{lead?.first_name} {lead?.last_name}</h2>
                      {lead?.ai_score && (
                        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded">
                          Score: {lead.ai_score}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                        lead?.status === "Qualified" ? "bg-green-500/20 text-green-400" :
                        lead?.status === "Hot" ? "bg-red-500/20 text-red-400" :
                        "bg-zinc-500/20 text-zinc-400"
                      }`}>
                        {lead?.status || "New"}
                      </span>
                    </div>
                    <p className="text-zinc-400 text-sm">{lead?.job_title || "Contact"} @ {lead?.company_name || "Unknown"}</p>
                    
                    <div className="flex items-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-zinc-500" />
                        <span className="text-white font-mono text-sm">{phoneNumber || "No phone"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-orange-400">🕐</span>
                        <div className="text-xs">
                          <span className="text-zinc-500">Best: </span>
                          <span className="text-white">{lead?.best_call_time || "10 AM - 12 PM"}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-zinc-500" />
                        <div className="text-xs">
                          <span className="text-zinc-500">Last contact: </span>
                          <span className="text-white">{getLastContactDays()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 border-l border-white/10 pl-6">
                  <p className="text-xs text-zinc-500 uppercase font-medium mb-2">Notes</p>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    {lead?.notes || lead?.pain_points || "No notes available. Add notes after the call to track important information."}
                  </p>
                </div>
              </div>
            </div>

            {/* Call Method Selection */}
            {phase === "ready" && (
              <>
                <div>
                  <p className="text-xs text-zinc-500 uppercase font-medium tracking-wider mb-4">Choose Call Method</p>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setCallMode("ai")}
                      className={`p-6 rounded-2xl border-2 text-left transition-all ${
                        callMode === "ai"
                          ? "border-purple-500 bg-purple-500/5"
                          : "border-white/10 bg-[#121212] hover:border-white/20"
                      }`}
                    >
                      <Sparkles className={`w-8 h-8 mb-4 ${callMode === "ai" ? "text-amber-400" : "text-zinc-500"}`} />
                      <p className={`text-lg font-semibold mb-2 ${callMode === "ai" ? "text-purple-400" : "text-white"}`}>
                        AI Call
                      </p>
                      <p className="text-sm text-zinc-500 leading-relaxed">
                        ZOEY handles the entire conversation autonomously. Real-time transcript, sentiment tracking, and auto-logged summary.
                      </p>
                    </button>
                    
                    <button
                      onClick={() => setCallMode("manual")}
                      className={`p-6 rounded-2xl border-2 text-left transition-all ${
                        callMode === "manual"
                          ? "border-pink-500 bg-pink-500/5"
                          : "border-white/10 bg-[#121212] hover:border-white/20"
                      }`}
                    >
                      <Phone className={`w-8 h-8 mb-4 ${callMode === "manual" ? "text-pink-400" : "text-zinc-500"}`} />
                      <p className={`text-lg font-semibold mb-2 ${callMode === "manual" ? "text-pink-400" : "text-white"}`}>
                        Manual Call
                      </p>
                      <p className="text-sm text-zinc-500 leading-relaxed">
                        Call using your phone. AI assists with real-time coaching, objection handling, and auto-logs the outcome.
                      </p>
                    </button>
                  </div>
                </div>

                <div className="bg-[#121212] rounded-2xl border border-white/10">
                  <button
                    onClick={() => setShowScriptPreview(!showScriptPreview)}
                    className="w-full flex items-center justify-between p-4 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <Bot className="w-5 h-5 text-amber-400" />
                      <span className="text-white font-medium">AI Script Preview</span>
                    </div>
                    {showScriptPreview ? (
                      <ChevronUp className="w-5 h-5 text-zinc-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-zinc-400" />
                    )}
                  </button>
                  
                  {showScriptPreview && (
                    <div className="px-4 pb-4 border-t border-white/5">
                      <div className="mt-4 p-4 bg-[#0a0a0a] rounded-xl">
                        <p className="text-sm text-zinc-300 leading-relaxed">
                          "Hi {lead?.first_name}, this is ZOEY from Unizar AI. I noticed you might be interested in streamlining your business operations. 
                          Do you have a few minutes to discuss how AI-powered solutions could help {lead?.company_name || "your company"} save time and reduce costs?"
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={callMode === "ai" ? startAICall : () => {
                    window.open(`tel:${phoneNumber}`, '_self');
                    toast.success("Opening phone dialer...");
                  }}
                  disabled={!phoneNumber || callLoading}
                  size="lg"
                  className="w-fit bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg px-8 py-6 rounded-2xl"
                >
                  {callLoading ? (
                    <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 mr-3" />
                  )}
                  {callMode === "ai" ? "Start AI Call" : "Start Manual Call"}
                </Button>
              </>
            )}

            {phase === "calling" && (
              <div className="bg-[#121212] rounded-2xl border border-purple-500/30 p-8 text-center">
                <div className="relative mx-auto w-32 h-32 mb-6">
                  <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-ping" />
                  <div className="absolute inset-4 rounded-full bg-purple-500/30 animate-pulse" />
                  <div className="absolute inset-8 rounded-full bg-purple-500/40 flex items-center justify-center">
                    <Volume2 className="w-10 h-10 text-purple-400 animate-pulse" />
                  </div>
                </div>
                
                <p className="text-3xl font-bold text-white mb-2">{formatDuration(callDuration)}</p>
                <p className="text-zinc-500 mb-6">
                  {callStatus === "initiating" ? "Connecting..." : `Speaking with ${lead?.first_name}...`}
                </p>
                
                <Button onClick={endCall} className="bg-red-500 hover:bg-red-600 text-white px-8">
                  <PhoneOff className="w-5 h-5 mr-2" />
                  End Call
                </Button>
              </div>
            )}

            {phase === "completed" && (
              <div className="bg-[#121212] rounded-2xl border border-green-500/30 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-400" />
                  <div>
                    <p className="text-xl font-semibold text-green-400">Call Completed</p>
                    <p className="text-zinc-400">Duration: {formatDuration(callDuration)}</p>
                  </div>
                </div>
                
                {callHistory?.last_call_summary && (
                  <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6">
                    <p className="text-xs text-zinc-500 uppercase mb-2">Call Summary</p>
                    <p className="text-zinc-300">{callHistory.last_call_summary}</p>
                  </div>
                )}
                
                <div className="flex gap-4">
                  <Button
                    onClick={() => { setPhase("ready"); setCallDuration(0); }}
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Call Again
                  </Button>
                  <Button
                    onClick={() => navigate(`/admin/leads/${leadId}`)}
                    variant="outline"
                    className="border-white/10 text-zinc-400"
                  >
                    Back to Lead
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="col-span-5 space-y-6">
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-6">
              {/* Call Statistics - Using shared component */}
              <SectionHeader title="Call Statistics" />
              <div className="mb-6">
                <ActionStatsGrid stats={callStats} columns={2} />
              </div>

              {/* Tabs - Using shared component */}
              <ActionTabs 
                tabs={tabs} 
                activeTab={activeTab} 
                onTabChange={setActiveTab}
                accentColor="purple"
              />

              {activeTab === "history" && (
                <div className="space-y-4 max-h-[400px] overflow-y-auto">
                  {allCalls.length > 0 ? allCalls.map((call, idx) => (
                    <div key={idx} className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-white font-medium">{formatDate(call.date || call.created_at)}</p>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          call.outcome === "voicemail" ? "bg-orange-500/20 text-orange-400" :
                          call.outcome === "connected" ? "bg-green-500/20 text-green-400" :
                          "bg-zinc-500/20 text-zinc-400"
                        }`}>
                          {call.outcome || call.status || "Completed"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                        <Clock className="w-3 h-3" />
                        <span>{call.duration ? formatDuration(call.duration) : "N/A"}</span>
                      </div>
                      {call.summary && <p className="text-sm text-zinc-400">{call.summary}</p>}
                    </div>
                  )) : (
                    <div className="text-center py-8">
                      <Phone className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                      <p className="text-sm text-zinc-500">No call history yet</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "tips" && (
                <div className="space-y-3">
                  <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
                    <p className="text-sm text-purple-300">
                      <span className="font-medium text-purple-400">Best time to call:</span> Based on previous interactions, 
                      {lead?.first_name} is most responsive between 10 AM - 12 PM.
                    </p>
                  </div>
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <p className="text-sm text-zinc-300">
                      <span className="font-medium text-white">Opening tip:</span> Reference their current pain points 
                      to establish relevance quickly.
                    </p>
                  </div>
                  <div className="bg-[#0a0a0a] rounded-xl p-4">
                    <p className="text-sm text-zinc-300">
                      <span className="font-medium text-white">Objection handling:</span> If they mention budget concerns, 
                      emphasize ROI and offer a pilot program.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === "automation" && (
                <div className="space-y-3">
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">Auto Follow-up</p>
                      <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded">Active</span>
                    </div>
                    <p className="text-sm text-zinc-500">Send email if no answer after 3 days</p>
                  </div>
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">Voicemail Detection</p>
                      <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded">Active</span>
                    </div>
                    <p className="text-sm text-zinc-500">Leave personalized voicemail automatically</p>
                  </div>
                  <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white font-medium">Call Recording</p>
                      <span className="text-xs text-green-400 bg-green-500/20 px-2 py-0.5 rounded">Active</span>
                    </div>
                    <p className="text-sm text-zinc-500">Record and transcribe all calls</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
